// WWI officers' quarters — the tidiest hut on the line. A neat timber hut
// under a terracotta gable (the home code from above) with a small porch on
// posts, a gunmetal stovepipe, a banner-red pennant on a corner flagpole and a
// low sandbag revetment guarding the front edge. No work station: officers
// keep their yard to themselves.
import { defineModel, styleV3, type Part } from "../../lib";

const { box, cyl, gable, poles, slab, ROOF_HOME } = styleV3;

// Muted WWI materials.
const KHAKI = "#7a6f4e";
const GUNMETAL = "#4a4d52";
const TIMBER_W = "#8b6438";
const TIMBER_LT = "#a3814f";
const MUD = "#5d4a33";
const CANVAS_W = "#cbbd93";
const RED = "#b03a3a";

const BASE = 0.12;                    // foundation slab top
const HZ = -0.22;                     // hut centre z (back of the plot)
const HW = 1.3, HD = 0.85;
const WALL_H = 0.55;
const FRONT = HZ + HD / 2;            // the porch face (+z)
const POLE_X = 0.8, POLE_Z = 0.62;    // the pennant flagpole, front-east corner

// The timber hut walls, `h` tall on the foundation.
function hut(h: number): Part {
  return box(HW, h, HD, TIMBER_W, [0, BASE + h / 2, HZ]);
}

// The small porch: two posts and a light plank canopy over the door.
function porch(): Part[] {
  return [
    cyl(0.03, 0.035, 0.42, 5, TIMBER_LT, [-0.24, BASE + 0.21, FRONT + 0.24]),
    cyl(0.03, 0.035, 0.42, 5, TIMBER_LT, [0.24, BASE + 0.21, FRONT + 0.24]),
    box(0.6, 0.04, 0.36, TIMBER_LT, [0, BASE + 0.45, FRONT + 0.14], [-0.12, 0, 0]),
    // Porch deck boards.
    box(0.6, 0.03, 0.3, TIMBER_LT, [0, BASE + 0.015, FRONT + 0.16]),
  ];
}

// The front face: a door flanked by two neat windows with sills.
function frontFace(): Part[] {
  const f = FRONT + 0.01;
  return [
    box(0.22, 0.4, 0.04, MUD, [0, BASE + 0.2, f]),
    box(0.14, 0.14, 0.04, CANVAS_W, [-0.42, BASE + 0.32, f]),
    box(0.14, 0.14, 0.04, CANVAS_W, [0.42, BASE + 0.32, f]),
    box(0.18, 0.03, 0.05, TIMBER_LT, [-0.42, BASE + 0.24, f]),
    box(0.18, 0.03, 0.05, TIMBER_LT, [0.42, BASE + 0.24, f]),
  ];
}

// The banner-red pennant on its corner flagpole.
function pennant(): Part[] {
  return [
    cyl(0.018, 0.024, 0.85, 5, TIMBER_LT, [POLE_X, BASE + 0.425, POLE_Z]),
    box(0.2, 0.09, 0.015, RED, [POLE_X + 0.11, BASE + 0.79, POLE_Z]),
  ];
}

// A low sandbag revetment: two staggered courses of squashed khaki bags.
function sandbags(): Part[] {
  const out: Part[] = [];
  const z = 0.72;
  for (let i = 0; i < 5; i++) {
    out.push(box(0.17, 0.075, 0.12, KHAKI, [-0.75 + i * 0.18, BASE + 0.038, z], [0, (i % 2 ? -0.08 : 0.08), 0]));
  }
  for (let i = 0; i < 4; i++) {
    out.push(box(0.17, 0.075, 0.12, "#6e6345", [-0.66 + i * 0.18, BASE + 0.11, z], [0, (i % 2 ? 0.1 : -0.06), 0]));
  }
  return out;
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [slab(1.94, 1.94)];
  if (s === 1) return [slab(1.94, 1.94), hut(0.26), ...poles(1.8, 1.8, 0.7)];
  if (s === 2) return [slab(1.94, 1.94), hut(WALL_H), ...frontFace().slice(0, 1), ...poles(1.8, 1.8, 1.2)];

  const eave = BASE + WALL_H;
  return [
    slab(1.94, 1.94),
    hut(WALL_H),
    // The neat terracotta gable — ridge along x, deep eaves. ONE part.
    gable(HD + 0.18, HW + 0.16, 0.42, ROOF_HOME, 0, eave, HZ, "x"),
    // Gunmetal stovepipe through the back roof plane.
    cyl(0.045, 0.05, 0.45, 6, GUNMETAL, [-0.42, eave + 0.3, HZ - 0.18]),
    ...porch(),
    ...frontFace(),
    ...pennant(),
    ...sandbags(),
    // The officer's footlocker on the porch deck.
    box(0.24, 0.12, 0.14, MUD, [0.42, BASE + 0.09, FRONT + 0.18]),
  ];
}

export const officersQuarters = defineModel({
  id: "officers-quarters",
  tiles: { w: 2, h: 2 },
  batchId: "wwi",
  name: "Officers' Quarters",
  tags: ["building", "wwi", "military", "quarters", "housing", "officers"],
  summary: "A neat timber hut with a terracotta roof, porch, pennant and sandbags.",
  description:
    "The officers' billet: a tidy timber hut under a terracotta gable with a " +
    "small porch, a gunmetal stovepipe, a banner-red pennant on a corner " +
    "flagpole and a low sandbag revetment along the front.",
  author: "Fable 5",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "foundation", label: "Foundation", parts: () => partsFor(0) },
    { id: "lower", label: "Lower", parts: () => partsFor(1) },
    { id: "upper", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
});
